import { MButton, MIcons, MSpinner } from "@material-dev/insights-portal-ui-components";
import withTitle from "@material-dev/insights-portal-ui-components/lib/feature-components/m-title";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useAppDispatch, useAppSelector } from "@store";
import { EncodingVersionDto } from "app/SharedApiClient";
import { COMMON_DATETIME_FORMAT } from "const/dateFormats";
import { useProjectParams } from "hooks";
import {
  getComparatorProjectEncodingVersions,
  getProjectByComparatorProjectId,
  getProjectByReferenceProjectId,
  getReferenceProjectEncodingVersions,
} from "slices/compareVersions/compareVersionsSlice";
import { maxStringBy } from "utils/arrayHelper";
import { getNormalizedOriginSource } from "utils/projectSourceHelper";
import { compareViewUrl, compareSelectionUrl } from "utils/routeHelper";
import ProjectSelectorDialog, { ExtendNormalizedShortProject } from "../ProjectSelectorDialog/ProjectSelectorDialog";
import { useStles } from "./CompareVersionsStyles";
import CompareVersionAlerts, { useCompareVersionValidation } from "./CompareVersionValidation";
import { CompareVersionValidationError } from "./CompareVersionValidation/types";
import { ProjectCompareTypes } from "./types";

type CompareVersionsParams = {
  referenceProjectId: string;
  referenceVersionId: string;
};
const getSelectedVersion = (versions: EncodingVersionDto[], versionId?: string) =>
  versions.find((x) => x.versionId == versionId);

const CompareSelection = () => {
  const { classes, cx } = useStles();
  const { comparatorProjectId, comparatorVersionId } = useProjectParams();
  const { referenceProjectId, referenceVersionId } = useParams<CompareVersionsParams>();
  const [isProjectSelectorOpened, setProjectSelectorOpened] = useState<boolean>(false);
  const navigate = useNavigate();
  const { comparatorEncodingVersions, referenceProject, referenceEncodingVersions, comparatorProject } = useAppSelector(
    (state) => state.compareVersions,
  );
  const [selectedProjectType, setSelectedProjectType] = useState<ProjectCompareTypes>(ProjectCompareTypes.reference);
  const dispatch = useAppDispatch();

  const referenceVersion = useMemo(
    () => getSelectedVersion(referenceEncodingVersions, referenceVersionId),
    [referenceVersionId, referenceEncodingVersions],
  );
  const latestReferenceVersionId = useMemo(
    () => maxStringBy(referenceEncodingVersions, (x) => x.versionId),
    [referenceEncodingVersions],
  );
  const latestComparatorVersionId = useMemo(
    () => maxStringBy(comparatorEncodingVersions, (x) => x.versionId),
    [comparatorEncodingVersions],
  );

  const comparatorVersion = useMemo(
    () => getSelectedVersion(comparatorEncodingVersions, comparatorVersionId),
    [comparatorVersionId, comparatorEncodingVersions],
  );

  useEffect(() => {
    if (referenceProjectId) {
      dispatch(getProjectByReferenceProjectId(referenceProjectId));
    }
  }, [referenceProjectId]);

  useEffect(() => {
    if (comparatorProjectId) {
      dispatch(getProjectByComparatorProjectId(comparatorProjectId));
    }
  }, [comparatorProjectId]);

  useEffect(() => {
    if (comparatorProject?.originSource && comparatorProjectId) {
      dispatch(
        getComparatorProjectEncodingVersions({
          originProjectId: comparatorProjectId,
          source: getNormalizedOriginSource(comparatorProject.id, comparatorProject.originSource),
        }),
      );
    }
  }, [comparatorProjectId, comparatorProject]);

  useEffect(() => {
    if (referenceProject?.originSource && referenceProjectId) {
      dispatch(
        getReferenceProjectEncodingVersions({
          originProjectId: referenceProjectId,
          source: getNormalizedOriginSource(referenceProject.id, referenceProject.originSource),
        }),
      );
    }
  }, [referenceProjectId, referenceProject]);
  const handleProjectSelectorClose = () => {
    setProjectSelectorOpened(false);
  };
  const handleProjectSelect = useCallback(
    (project: ExtendNormalizedShortProject) => {
      if (referenceProjectId && referenceVersionId) {
        if (selectedProjectType === ProjectCompareTypes.comparator) {
          navigate(
            compareSelectionUrl(referenceProjectId, referenceVersionId, project.originProjectId, project.versionId),
            {
              replace: true,
            },
          );
        } else {
          navigate(
            compareSelectionUrl(project.originProjectId, project.versionId, comparatorProjectId, comparatorVersionId),
            {
              replace: true,
            },
          );
        }
      }
      setProjectSelectorOpened(false);
    },
    [selectedProjectType, referenceProjectId, referenceVersionId, comparatorProjectId, comparatorVersionId],
  );

  const handleBackClick = () => {
    navigate(-1);
  };
  const handleChangeVersionClick = (projectType: ProjectCompareTypes) => () => {
    setSelectedProjectType(projectType);
    setProjectSelectorOpened(true);
  };

  const handleSeeComparisonClick = () => {
    if (referenceProjectId && referenceVersionId && comparatorVersionId) {
      navigate(compareViewUrl(referenceProjectId, referenceVersionId, comparatorVersionId));
    }
  };

  const errors = useCompareVersionValidation(referenceVersion, comparatorVersion);
  const theSameVersionError = Boolean(errors.find((e) => e.type === CompareVersionValidationError.SameVersions));
  const compareDisabled =
    !referenceProject ||
    !comparatorProject ||
    !referenceVersion ||
    !comparatorVersion ||
    theSameVersionError ||
    referenceProjectId !== comparatorProjectId ||
    referenceVersionId === comparatorVersionId ||
    !(referenceVersionId === latestReferenceVersionId || comparatorVersionId === latestComparatorVersionId);

  return (
    <div className={classes.root}>
      <ProjectSelectorDialog
        onClose={handleProjectSelectorClose}
        isOpen={isProjectSelectorOpened}
        onVersionSelect={handleProjectSelect}
        projectId={selectedProjectType === ProjectCompareTypes.reference ? referenceProjectId : comparatorProjectId}
        versionId={selectedProjectType === ProjectCompareTypes.reference ? referenceVersionId : comparatorVersionId}
      />
      <div className={classes.header}>
        <div className={classes.headerTitle}>
          <MButton className={classes.btnBack} onClick={handleBackClick}>
            <MIcons.ArrowBack className={classes.backIcon} />
            Back
          </MButton>
          <div className={classes.title}>Select Versions to Compare</div>
        </div>
      </div>
      <div className={classes.content}>
        <div className={classes.validationWrapper}>
          <CompareVersionAlerts errors={errors} />
        </div>
        <div className={classes.cardsWrapper}>
          <div className={classes.card}>
            <div className={cx(classes.cardTitleWrapper, classes.cardTitleFirst)}>
              <div className={cx(classes.cardTitle)}>Reference Version</div>
            </div>
            <div className={cx(classes.versionWrapper, classes.referenceVersion)}>
              <MSpinner className={classes.spinner} isLoading={!referenceProject || !referenceVersion} />
              {referenceProject && referenceVersion && (
                <>
                  <div className={classes.projectTitle}>
                    {`${referenceProject.originProjectId ?? ""} ${referenceProject.name ?? ""}`}
                  </div>
                  <div className={classes.versionTitle}>
                    {`${referenceVersion.versionDate?.toDateFormat(COMMON_DATETIME_FORMAT) ?? ""} `}
                    <span>Version</span>
                  </div>
                </>
              )}
            </div>
            <MButton
              color="primary"
              variant="outlined"
              className={classes.btnChangeVersion}
              onClick={handleChangeVersionClick(ProjectCompareTypes.reference)}>
              Change Version
            </MButton>
          </div>
          <div className={classes.card}>
            <div className={cx(classes.cardTitleWrapper, classes.cardTitleSecond)}>
              <div className={cx(classes.cardTitle)}>Comparator Version</div>
            </div>
            {!comparatorProjectId && !comparatorVersionId && (
              <div className={cx(classes.versionWrapper, classes.selectVersionWrapper)}>
                <div className={classes.btnAdd} onClick={handleChangeVersionClick(ProjectCompareTypes.comparator)}>
                  <MIcons.Add className={classes.plusIcon} />
                </div>
                <div className={classes.addTitle}>Select a version from a dataset</div>
              </div>
            )}
            {comparatorProjectId && comparatorVersionId && (
              <div className={cx(classes.versionWrapper, classes.referenceVersion)}>
                <MSpinner className={classes.spinner} isLoading={!comparatorProject || !comparatorVersion} />
                {comparatorProject && comparatorVersion && (
                  <>
                    <div className={classes.projectTitle}>
                      {`${comparatorProject.originProjectId ?? ""} ${comparatorProject.name ?? ""}`}
                    </div>
                    <div className={classes.versionTitle}>
                      {`${comparatorVersion.versionDate?.toDateFormat(COMMON_DATETIME_FORMAT) ?? ""} `}
                      <span>Version</span>
                    </div>
                  </>
                )}
              </div>
            )}
            {comparatorProject && (
              <MButton
                color="primary"
                variant="outlined"
                className={classes.btnChangeVersion}
                onClick={handleChangeVersionClick(ProjectCompareTypes.comparator)}>
                Change Version
              </MButton>
            )}
          </div>
          <MButton
            color="primary"
            variant="contained"
            className={classes.btnCompare}
            disabled={compareDisabled}
            onClick={handleSeeComparisonClick}>
            See Comparisons
          </MButton>
        </div>
      </div>
    </div>
  );
};

const PAGE_TITLE = "Data Manager - Compare Versions";
export default withTitle(CompareSelection, PAGE_TITLE);
